import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { SiteContent } from '../types/siteContent';
import { DEFAULT_SITE_CONTENT } from '../config/siteContent';
import { getSiteContentFromFirebase, saveSiteContentToFirebase } from '../services/firebaseService';
import { deepMerge } from '../utils/deepMerge';

const STORAGE_KEY = 'varna_imoti_site_content_v1';

interface SiteContentContextType {
  content: SiteContent;
  updateSection: <K extends keyof SiteContent>(section: K, updates: Partial<SiteContent[K]>) => void;
  updateContent: (newContent: SiteContent) => void;
  saveContent: (customContent?: SiteContent) => Promise<boolean>;
  resetToDefaults: () => Promise<void>;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  lastSavedAt: string | null;
}

const SiteContentContext = createContext<SiteContentContextType | undefined>(undefined);

export const SiteContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Synchronous initialization for 0ms visual delay / high performance
  const [content, setContent] = useState<SiteContent>(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const merged: SiteContent = deepMerge(DEFAULT_SITE_CONTENT, parsed);

        // Sanitize any legacy hardcoded placeholder phones and emails that might be in cached storage
        if (merged.navigation?.topBar?.phone === '+359 52 800 900') merged.navigation.topBar.phone = DEFAULT_SITE_CONTENT.navigation.topBar.phone;
        if (merged.navigation?.topBar?.email === 'office@varna-imoti.bg' || merged.navigation?.topBar?.email === 'hrisi020023@gmail.com') merged.navigation.topBar.email = DEFAULT_SITE_CONTENT.navigation.topBar.email;
        if (merged.contactsPage?.phone1 === '+359 52 800 900') merged.contactsPage.phone1 = DEFAULT_SITE_CONTENT.contactsPage.phone1;
        if (merged.contactsPage?.phone2 === '+359 888 123 456') merged.contactsPage.phone2 = DEFAULT_SITE_CONTENT.contactsPage.phone2;
        if (merged.contactsPage?.email === 'office@varna-imoti.bg' || merged.contactsPage?.email === 'hrisi020023@gmail.com') merged.contactsPage.email = DEFAULT_SITE_CONTENT.contactsPage.email;
        if (merged.footer?.phone === '+359 52 800 900') merged.footer.phone = DEFAULT_SITE_CONTENT.footer.phone;
        if (merged.footer?.email === 'office@varna-imoti.bg' || merged.footer?.email === 'hrisi020023@gmail.com') merged.footer.email = DEFAULT_SITE_CONTENT.footer.email;
        if (merged.branding?.logoUrl === '/imotencenterlogo.png' || !merged.branding?.logoUrl) {
          merged.branding = {
            ...merged.branding,
            logoUrl: '/imotencenterlogo-cropped.png'
          };
        }
        if (merged.hero?.bgImage === '/hero.jpg' || !merged.hero?.bgImage) {
          merged.hero = {
            ...merged.hero,
            bgImage: '/hero.webp'
          };
        }
        if (merged.seo?.defaultOgImage === '/hero.jpg') {
          merged.seo.defaultOgImage = '/hero.webp';
        }
        if (merged.aboutSection?.reviewsBadge) {
          merged.aboutSection.reviewsBadge = '';
        }

        return merged;
      }
    } catch (e) {
      console.warn('Could not read site content cache:', e);
    }
    return DEFAULT_SITE_CONTENT;
  });

  const [savedSnapshot, setSavedSnapshot] = useState<string>(() => JSON.stringify(content));
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // Apply fonts and CSS variables immediately to document element
  useEffect(() => {
    const root = document.documentElement;
    if (content.branding?.headingFont) {
      root.style.setProperty('--font-heading', `"${content.branding.headingFont}", sans-serif`);
    }
    if (content.branding?.bodyFont) {
      root.style.setProperty('--font-body', `"${content.branding.bodyFont}", sans-serif`);
    }
    if (content.branding?.siteTitle) {
      document.title = `${content.branding.siteTitle} - Професионални Имотни Услуги`;
    }
  }, [content.branding?.headingFont, content.branding?.bodyFont, content.branding?.siteTitle]);

  // Background fetch from server on mount
  useEffect(() => {
    let isMounted = true;
    const loadServerContent = async () => {
      try {
        // Try Firebase Firestore first
        const fbData = await getSiteContentFromFirebase();
        if (fbData && typeof fbData === 'object' && Object.keys(fbData).length > 0) {
          if (isMounted) {
            setContent(prev => {
              const merged = deepMerge(DEFAULT_SITE_CONTENT, prev, fbData);
              if (merged.aboutSection?.reviewsBadge) {
                merged.aboutSection.reviewsBadge = '';
              }
              localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
              setSavedSnapshot(JSON.stringify(merged));
              return merged;
            });
            return;
          }
        }

        // Fallback to Express backend API if running
        try {
          const res = await fetch('/api/site-content');
          if (res.ok) {
            const data = await res.json();
            if (data && typeof data === 'object' && Object.keys(data).length > 0) {
              if (isMounted) {
                setContent(prev => {
                  const merged = deepMerge(DEFAULT_SITE_CONTENT, prev, data);
                  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
                  setSavedSnapshot(JSON.stringify(merged));
                  return merged;
                });
              }
            }
          }
        } catch {
          // Silent fallback when API route does not exist
        }
      } catch (err) {
        console.warn('Could not fetch server site content, using local store:', err);
      }
    };

    loadServerContent();
    return () => {
      isMounted = false;
    };
  }, []);

  const updateSection = useCallback(<K extends keyof SiteContent>(section: K, updates: Partial<SiteContent[K]>) => {
    setContent(prev => {
      const next = {
        ...prev,
        [section]: {
          ...(typeof prev[section] === 'object' && prev[section] !== null ? prev[section] : {}),
          ...updates
        }
      };
      // Immediately cache to localStorage so user doesn't lose changes on refresh
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        console.warn('LocalStorage quota or write error:', e);
      }
      return next;
    });
  }, []);

  const updateContent = useCallback((newContent: SiteContent) => {
    setContent(newContent);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newContent));
    } catch (e) {
      console.warn('LocalStorage write error:', e);
    }
  }, []);

  const saveContent = useCallback(async (customContent?: SiteContent): Promise<boolean> => {
    const targetContent = customContent || content;
    setIsSaving(true);
    let firestoreSuccess = false;
    let serverSuccess = false;

    // 1. Save to localStorage immediately as local cache
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(targetContent));
    } catch (lsErr) {
      console.warn('LocalStorage save warning:', lsErr);
    }

    // 2. Persist to Firebase Firestore
    try {
      await saveSiteContentToFirebase(targetContent);
      firestoreSuccess = true;
    } catch (fbErr) {
      console.error('Firebase Firestore site content save error:', fbErr);
    }

    // 3. Persist to server backend API (if running in full-stack dev environment)
    try {
      const res = await fetch('/api/site-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(targetContent),
      });
      if (res.ok) {
        serverSuccess = true;
      }
    } catch (serverErr) {
      // Ignored if purely static Cloudflare Pages deployment without /api/site-content route
    }

    setIsSaving(false);

    if (firestoreSuccess || serverSuccess) {
      setSavedSnapshot(JSON.stringify(targetContent));
      setLastSavedAt(new Date().toLocaleTimeString('bg-BG'));
      return true;
    } else {
      // LocalStorage succeeded as fallback
      setSavedSnapshot(JSON.stringify(targetContent));
      setLastSavedAt(new Date().toLocaleTimeString('bg-BG'));
      return true;
    }
  }, [content]);

  const resetToDefaults = useCallback(async () => {
    setIsSaving(true);
    try {
      setContent(DEFAULT_SITE_CONTENT);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_SITE_CONTENT));
      setSavedSnapshot(JSON.stringify(DEFAULT_SITE_CONTENT));

      // Reset in Firebase Firestore as well
      try {
        await saveSiteContentToFirebase(DEFAULT_SITE_CONTENT);
      } catch (fbErr) {
        console.warn('Could not reset site content in Firestore:', fbErr);
      }

      // Reset on server if running
      try {
        await fetch('/api/site-content/reset', { method: 'POST' });
      } catch {}

      setLastSavedAt(new Date().toLocaleTimeString('bg-BG'));
    } catch (e) {
      console.error('Reset error:', e);
    } finally {
      setIsSaving(false);
    }
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(content) !== savedSnapshot;
  }, [content, savedSnapshot]);

  return (
    <SiteContentContext.Provider
      value={{
        content,
        updateSection,
        updateContent,
        saveContent,
        resetToDefaults,
        isSaving,
        hasUnsavedChanges,
        lastSavedAt,
      }}
    >
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = (): SiteContentContextType => {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within a SiteContentProvider');
  }
  return context;
};
